import * as Sentry from '@sentry/nextjs';

import { sentryConfig } from '../utils/sentry-config';

/**
 * Telemetry for the "stale-but-interactive" failure mode introduced by
 * server-side TanStack hydration (PR #2805 and similar).
 *
 * When a query that already has cached data (e.g. from an RSC
 * `setQueryData` + `<HydrationBoundary>` handoff, or from a previous
 * successful fetch) errors on a background refetch, the user keeps
 * seeing the cached payload while every interaction silently fails
 * against the live API. There is no other place in the cache pipeline
 * where this state is directly observable — `captureTanstackError`
 * fires for the error itself but does not tell us the cache was
 * non-empty when it happened.
 *
 * This helper is wired into `QueryCache.onError` and emits:
 * - a breadcrumb on every occurrence (so subsequent captured
 *   exceptions carry the stale-data context cheaply), and
 * - a `captureMessage` event so the failure mode is searchable in
 *   Sentry as `tanstack.stale_hydrated_cache_error` without having
 *   to inspect individual exception stacks.
 *
 * Every entry-point is wrapped in `try { … } catch {}` and short-circuits
 * when Sentry is disabled — observability must never interrupt the
 * recovery flow it observes.
 */

const BREADCRUMB_CATEGORY = 'tanstack_stale_cache';

export const STALE_HYDRATED_CACHE_ATTR = {
  SOURCE_KEY: 'tanstack.stale_cache.source_key',
  API_STATUS: 'tanstack.stale_cache.api_status',
  FAILURE_COUNT: 'tanstack.stale_cache.failure_count',
  AGE_MS: 'tanstack.stale_cache.data_age_ms',
  CLERK_REFRESH_ATTEMPTED: 'tanstack.stale_cache.clerk_refresh_attempted',
} as const;

export const STALE_HYDRATED_CACHE_MESSAGE =
  'tanstack.stale_hydrated_cache_error';

export interface ObserveStaleHydratedCacheErrorArgs {
  /**
   * First segment of the failing query key, when stringifiable. Lets
   * dashboards group by feature (`deals`, `packages`, `tasks`, …).
   */
  sourceKey?: string;
  /** HTTP status when the error is an `ApiError`; `undefined` otherwise. */
  apiStatus?: number;
  /** `query.state.fetchFailureCount` at the moment `onError` fired. */
  failureCount: number;
  /**
   * `Date.now() - query.state.dataUpdatedAt`, i.e. how long the cached
   * payload has been live by the time the refetch failed. For
   * SSR-hydrated entries this is roughly time-since-hydration.
   */
  dataAgeMs: number;
  /**
   * `true` when the cache handler took the Clerk-refresh branch for
   * this error. Lets us distinguish "401 the refresh tried to recover"
   * from "non-401 we never tried to recover".
   */
  clerkRefreshAttempted: boolean;
}

function addBreadcrumb(data: Record<string, unknown>): void {
  try {
    Sentry.addBreadcrumb({
      category: BREADCRUMB_CATEGORY,
      message: STALE_HYDRATED_CACHE_MESSAGE,
      level: 'warning',
      data,
    });
  } catch {
    // Observability must never break the recovery path.
  }
}

/**
 * Record a stale-but-interactive cache failure. Safe to call on every
 * `QueryCache.onError` invocation — the caller is expected to gate on
 * `query.state.data !== undefined`.
 *
 * Fires a breadcrumb AND a `captureMessage` (level `warning`) so the
 * failure mode is both searchable by event count and joinable as
 * context onto downstream exceptions.
 */
export function observeStaleHydratedCacheError({
  sourceKey,
  apiStatus,
  failureCount,
  dataAgeMs,
  clerkRefreshAttempted,
}: ObserveStaleHydratedCacheErrorArgs): void {
  if (!sentryConfig.enabled) return;
  if (typeof window === 'undefined') return;

  const data: Record<string, unknown> = {
    failureCount,
    dataAgeMs,
    clerkRefreshAttempted,
  };
  if (sourceKey) data.sourceKey = sourceKey;
  if (typeof apiStatus === 'number') data.apiStatus = apiStatus;

  addBreadcrumb(data);

  try {
    Sentry.withScope(scope => {
      if (sourceKey) {
        scope.setTag(STALE_HYDRATED_CACHE_ATTR.SOURCE_KEY, sourceKey);
      }
      if (typeof apiStatus === 'number') {
        scope.setTag(STALE_HYDRATED_CACHE_ATTR.API_STATUS, apiStatus);
      }
      scope.setTag(STALE_HYDRATED_CACHE_ATTR.FAILURE_COUNT, failureCount);
      scope.setTag(STALE_HYDRATED_CACHE_ATTR.AGE_MS, dataAgeMs);
      scope.setTag(
        STALE_HYDRATED_CACHE_ATTR.CLERK_REFRESH_ATTEMPTED,
        clerkRefreshAttempted ? 'true' : 'false'
      );
      scope.setLevel('warning');
      Sentry.captureMessage(STALE_HYDRATED_CACHE_MESSAGE);
    });
  } catch {
    // Observability must never break the recovery path.
  }
}
